/**
 * Playout control sessions' routes (ACP-D52, gfx/docs/plans/playout.md §5; spread into `ROUTES` by `routes.ts`): a
 * user opens a `control` session on breakout's relay, hands its capability link to renderers, and users of the tenant
 * attach to it over the relay's WebSocket (`breakout.ts`, docs/gfx-breakout/control.md) — one holding the control lease,
 * the rest watching. The commands and events run on the relay, not here. Cloud only.
 *
 * Permissions (Q5): opening and managing a session needs `graphics.playout` tenant-wide; reading the tenant's sessions
 * needs `graphics.read` (any user who may watch one). The management routes (link, renew, disconnect, close) are the
 * session owner's and the current lease holder's: anyone else is answered `not-found`, as is a closed or expired
 * session. Unlike breakout's, control sessions are visible to every user of the tenant who may read graphics: a watcher
 * must find the session to attach to it. The staff tenant (D39) is refused the writes (`graphics` tag).
 *
 * Caps (the relay's config): 20 renderers per session, 5 open control sessions per user, 25 per tenant (counted apart
 * from breakout's). A session lasts at most 7 days from its opening or its last renewal (Q1).
 */
import {
  breakoutRemotePathWireSchema,
  breakoutSessionPathWireSchema,
  closeBreakoutSessionResponseWireSchema,
  controlSessionDetailWireSchema,
  disconnectBreakoutRemoteResponseWireSchema,
  listControlSessionsResponseWireSchema,
  openBreakoutSessionRequestWireSchema,
  openControlSessionResponseWireSchema,
  renewControlSessionRequestWireSchema,
  renewControlSessionResponseWireSchema,
  setBreakoutLinkRequestWireSchema,
  setControlLinkResponseWireSchema,
} from './breakout.js';
import type { RouteDef } from './routes.js';

/** Where the control-session routes live; the relay's dev server serves them there too. */
export const PLAYOUT_PATH_PREFIX = '/graphics/playout';

const MANAGERS = 'The session’s owner and its current lease holder only: for anyone else, and for a closed, expired or unknown session, `not-found`.';

export const GRAPHICS_PLAYOUT_ROUTES = {
  openGraphicsPlayoutSession: {
    id: 'openGraphicsPlayoutSession',
    method: 'POST',
    path: '/graphics/playout/sessions',
    tag: 'graphics',
    summary: 'Open a playout control session',
    description:
      'A new control session owned by the caller, who holds its control lease, lasting 7 days unless renewed, with its first capability link. The link’s token is answered ONCE (the relay keeps only its hash); renderers join with it. Users then attach to the relay with their own tokens. `conflict` when the caller already has 5 open control sessions or the tenant 25.',
    params: null,
    query: null,
    body: openBreakoutSessionRequestWireSchema,
    response: openControlSessionResponseWireSchema,
    successStatus: 201,
    permission: 'graphics.playout',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'conflict'],
  },
  listGraphicsPlayoutSessions: {
    id: 'listGraphicsPlayoutSessions',
    method: 'GET',
    path: '/graphics/playout/sessions',
    tag: 'graphics',
    summary: 'List the tenant’s playout control sessions',
    description: 'Every open control session of the caller’s tenant, newest first, with its lease holder: what a user may attach to (as a watcher with `graphics.read`, as a controller with `graphics.playout`).',
    params: null,
    query: null,
    body: null,
    response: listControlSessionsResponseWireSchema,
    successStatus: 200,
    permission: 'graphics.read',
    permissionScope: 'tenant',
    errors: [],
  },
  getGraphicsPlayoutSession: {
    id: 'getGraphicsPlayoutSession',
    method: 'GET',
    path: '/graphics/playout/sessions/{sessionId}',
    tag: 'graphics',
    summary: 'Get a playout control session',
    description: 'The session (its lease: holder, since, whether the holder is attached) and its renderers with their last reports. A closed, expired or unknown session is `not-found`.',
    params: breakoutSessionPathWireSchema,
    query: null,
    body: null,
    response: controlSessionDetailWireSchema,
    successStatus: 200,
    permission: 'graphics.read',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found'],
  },
  setGraphicsPlayoutLink: {
    id: 'setGraphicsPlayoutLink',
    method: 'POST',
    path: '/graphics/playout/sessions/{sessionId}/link',
    tag: 'graphics',
    summary: 'Make a new renderer link, or stop admitting renderers',
    description: `\`admit: true\` makes a new capability link and answers its token once; the previous link stops admitting at once. \`admit: false\`: no new renderers until a new link is made. Joined renderers are not affected. ${MANAGERS}`,
    params: breakoutSessionPathWireSchema,
    query: null,
    body: setBreakoutLinkRequestWireSchema,
    response: setControlLinkResponseWireSchema,
    successStatus: 200,
    permission: 'graphics.playout',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'not-found'],
  },
  renewGraphicsPlayoutSession: {
    id: 'renewGraphicsPlayoutSession',
    method: 'POST',
    path: '/graphics/playout/sessions/{sessionId}/renew',
    tag: 'graphics',
    summary: 'Renew a playout control session',
    description: `The body is \`{}\`. The session now ends 7 days from now, and its link and every renderer’s credential with it; every socket of the session hears \`renewed\`. ${MANAGERS}`,
    params: breakoutSessionPathWireSchema,
    query: null,
    body: renewControlSessionRequestWireSchema,
    response: renewControlSessionResponseWireSchema,
    successStatus: 200,
    permission: 'graphics.playout',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'not-found'],
  },
  disconnectGraphicsPlayoutRemote: {
    id: 'disconnectGraphicsPlayoutRemote',
    method: 'DELETE',
    path: '/graphics/playout/sessions/{sessionId}/remotes/{remoteId}',
    tag: 'graphics',
    summary: 'Disconnect one renderer',
    description: `Revokes the renderer’s credential and closes its socket (it is told \`revoked\` first); the users hear \`remote.left\`. ${MANAGERS} An unknown renderer is \`not-found\`.`,
    params: breakoutRemotePathWireSchema,
    query: null,
    body: null,
    response: disconnectBreakoutRemoteResponseWireSchema,
    successStatus: 200,
    permission: 'graphics.playout',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found'],
  },
  closeGraphicsPlayoutSession: {
    id: 'closeGraphicsPlayoutSession',
    method: 'DELETE',
    path: '/graphics/playout/sessions/{sessionId}',
    tag: 'graphics',
    summary: 'Close a playout control session',
    description: `Revokes the link and every credential, tells every socket (renderers and users) \`closed\` and closes it, and forgets the session. Renderers keep showing what they show (free-running). ${MANAGERS}`,
    params: breakoutSessionPathWireSchema,
    query: null,
    body: null,
    response: closeBreakoutSessionResponseWireSchema,
    successStatus: 200,
    permission: 'graphics.playout',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found'],
  },
} as const satisfies Record<string, RouteDef>;
